import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar } from 'lucide-react';
import { presupuestosAPI } from '../../api/api';
import { esMultievento, jornadasOrdenadas } from '../../utils/multieventoCabecera';
import { hoyMadrid } from '../../utils/fechas';

// "Ver en el calendario" de la cabecera de la ficha: abre el calendario en el
// día del evento con su ventana ya abierta (?fecha=&evento=) y, si ese día hay
// otros eventos confirmados, lo dice ("Ese día: 2 eventos más").

const ES_FECHA = /^\d{4}-\d{2}-\d{2}$/;

// Fecha que se abre en el calendario: la del evento; en multievento, la de la
// primera jornada de hoy en adelante o, si todas pasaron, la primera.
export const fechaParaCalendario = (presupuesto) => {
  if (!presupuesto) return '';
  if (esMultievento(presupuesto)) {
    const fechas = jornadasOrdenadas(presupuesto)
      .map(j => String(j?.fecha ?? '').slice(0, 10))
      .filter(f => ES_FECHA.test(f));
    if (fechas.length === 0) return '';
    const hoy = hoyMadrid();
    return fechas.find(f => f >= hoy) || fechas[0];
  }
  const f = String(presupuesto.fecha_evento || '').slice(0, 10);
  return ES_FECHA.test(f) ? f : '';
};

// Eventos confirmados de cada día, compartidos entre fichas durante 5 minutos
// (una sola petición por fecha aunque se abran varias fichas del mismo día)
const CACHE_MS = 5 * 60 * 1000;
const cacheDias = new Map(); // fecha -> { hora, promesa }

const eventosConfirmadosDelDia = (fecha) => {
  const guardado = cacheDias.get(fecha);
  if (guardado && Date.now() - guardado.hora < CACHE_MS) return guardado.promesa;
  const promesa = presupuestosAPI
    .getCalendario({ estado: 'confirmado', fecha_inicio: fecha, fecha_fin: `${fecha}T23:59:59` })
    .then(res => {
      const eventos = Array.isArray(res?.data?.eventos) ? res.data.eventos : [];
      return eventos.filter(e => String(e?.fecha_evento || '').slice(0, 10) === fecha);
    });
  cacheDias.set(fecha, { hora: Date.now(), promesa });
  // Si falla, no se guarda: la próxima ficha lo vuelve a intentar
  promesa.catch(() => { cacheDias.delete(fecha); });
  return promesa;
};

const VerEnCalendario = ({ presupuesto, className = '' }) => {
  const navigate = useNavigate();
  const fecha = fechaParaCalendario(presupuesto);
  const presupuestoId = presupuesto?.id || '';
  const [otros, setOtros] = useState(null); // { fecha, n }

  useEffect(() => {
    if (!fecha) return undefined;
    let vigente = true;
    eventosConfirmadosDelDia(fecha)
      .then(eventos => {
        if (!vigente) return;
        // Otros presupuestos ese día (una jornada de multievento cuenta como un evento)
        const claves = new Set(eventos
          .filter(e => (e.presupuesto_id || e.id) !== presupuestoId)
          .map(e => `${e.presupuesto_id || e.id}|${e.jornada_id || ''}`));
        setOtros({ fecha, n: claves.size });
      })
      .catch(() => { if (vigente) setOtros(null); });
    return () => { vigente = false; };
  }, [fecha, presupuestoId]);

  if (!fecha || !presupuestoId) return null;
  const n = otros && otros.fecha === fecha ? otros.n : 0;

  return (
    <span className={`inline-flex flex-wrap items-center gap-x-2 gap-y-0.5 ${className}`}>
      <button
        type="button"
        onClick={() => navigate(`/calendario-eventos?fecha=${fecha}&evento=${encodeURIComponent(presupuestoId)}`)}
        className="inline-flex items-center gap-1 text-xs font-semibold text-[#11704F] hover:text-[#0B5139] hover:underline"
        title="Abrir el calendario en el día del evento, con su ventana abierta"
        data-uso="presupuesto.ver_calendario"
        data-guia="ver-calendario"
        data-testid="ver-en-calendario"
      >
        <Calendar className="w-3.5 h-3.5" /> Ver en el calendario
      </button>
      {n > 0 && (
        <span className="text-xs text-[#5B6B64]" data-testid="ver-en-calendario-otros">
          {`Ese día: ${n} ${n === 1 ? 'evento más' : 'eventos más'}`}
        </span>
      )}
    </span>
  );
};

export default VerEnCalendario;
